import {
  STREAM_EVENT,
  type ChatToolLoadingStateData,
  type ContractMessagePart,
  type ItineraryUpdate,
  type EnrichmentCompleteEvent,
  type HotelSearchResultsData,
  type ExperienceSearchResultsData,
  type HotelSearchErrorData,
  type ExperienceSearchErrorData,
  type ChatUiActionData,
  type StatusEventData,
} from 'tripPlanner/api/types'

/**
 * StreamEvent represents all possible parsed events from the message stream.
 */
export type StreamEvent =
  | { event: typeof STREAM_EVENT.MESSAGE_START; messageId: string; role: 'ASSISTANT' | 'USER' | string }
  | { event: typeof STREAM_EVENT.PARTS_UPDATE; messageId: string; parts: Array<ContractMessagePart> }
  | { event: typeof STREAM_EVENT.MESSAGE_END; messageId: string; parts: Array<ContractMessagePart>; oaiConversationId?: string; rawContent?: string }
  | { event: typeof STREAM_EVENT.ITINERARY_UPDATE_STARTED }
  | { event: typeof STREAM_EVENT.ITINERARY_UPDATES; updates: Array<ItineraryUpdate> }
  | { event: typeof STREAM_EVENT.CONTENT_COMPLETE }
  | { event: typeof STREAM_EVENT.ENRICHMENT_COMPLETE; data: EnrichmentCompleteEvent }
  | { event: typeof STREAM_EVENT.CHAT_LOADING_STATE; data: ChatToolLoadingStateData }
  | { event: typeof STREAM_EVENT.HOTEL_SEARCH_RESULTS; data: HotelSearchResultsData }
  | { event: typeof STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS; data: ExperienceSearchResultsData }
  | { event: typeof STREAM_EVENT.HOTEL_SEARCH_ERROR; data: HotelSearchErrorData }
  | { event: typeof STREAM_EVENT.EXPERIENCE_SEARCH_ERROR; data: ExperienceSearchErrorData }
  | { event: typeof STREAM_EVENT.CHAT_UI_ACTION; data: ChatUiActionData }
  | { event: typeof STREAM_EVENT.STATUS; data: StatusEventData }
  | { event: typeof STREAM_EVENT.HEARTBEAT }
  | { event: typeof STREAM_EVENT.TOKEN_BATCH; text: string }

/**
 * Type guard for EnrichmentCompleteEvent from parsed JSON objects.
 */
function isEnrichmentCompleteEvent(
  obj: Record<string, unknown>,
): obj is EnrichmentCompleteEvent {
  return obj.entityType === 'stop' || obj.entityType === 'item'
}

/**
 * Shared shape guard for hotel/experience search results payloads.
 */
function isSearchResultsDataShape(
  data: unknown,
): data is {
  searchQuery: string
  offers: Array<{ id: string; name: string }>
} {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  return (
    typeof obj.searchQuery === 'string' &&
    Array.isArray(obj.offers) &&
    obj.offers.every((offer: unknown) => {
      if (typeof offer !== 'object' || offer === null) return false
      const o = offer as Record<string, unknown>
      return typeof o.id === 'string' && typeof o.name === 'string'
    })
  )
}

/**
 * Type guard for HotelSearchResultsData from parsed JSON objects.
 */
function isHotelSearchResultsData(
  data: unknown,
): data is HotelSearchResultsData {
  return isSearchResultsDataShape(data)
}

function isExperienceSearchResultsData(
  data: unknown,
): data is ExperienceSearchResultsData {
  return isSearchResultsDataShape(data)
}

/**
 * Shared shape guard for hotel/experience search error payloads.
 */
function isSearchErrorDataShape(
  data: unknown,
): data is { message: string } {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  return typeof obj.message === 'string'
}

/**
 * Type guard for HotelSearchErrorData from parsed JSON objects.
 */
function isHotelSearchErrorData(
  data: unknown,
): data is HotelSearchErrorData {
  return isSearchErrorDataShape(data)
}

function isExperienceSearchErrorData(
  data: unknown,
): data is ExperienceSearchErrorData {
  return isSearchErrorDataShape(data)
}

function isChatToolLoadingStateData(
  data: unknown,
): data is ChatToolLoadingStateData {
  if (typeof data !== 'object' || data === null) return false

  const obj = data as Record<string, unknown>
  return (
    obj.kind === 'tool_call' &&
    (obj.phase === 'start' || obj.phase === 'complete') &&
    typeof obj.key === 'string' &&
    typeof obj.callId === 'string'
  )
}

function isStatusEventData(obj: Record<string, unknown>): obj is StatusEventData {
  const { phase } = obj
  if (phase === 'reasoning') return true
  if (phase === 'tool_calling' && typeof obj.tool === 'string') return true
  return false
}

function isChatUiActionData(data: unknown): data is ChatUiActionData {
  if (typeof data !== 'object' || data === null) return false
  const obj = data as Record<string, unknown>
  if (!(obj.action === 'set_right_panel_view' &&
    (obj.view === 'map' || obj.view === 'itinerary'))) {
    return false
  }
  const hasLat = Object.prototype.hasOwnProperty.call(obj, 'lat')
  const hasLng = Object.prototype.hasOwnProperty.call(obj, 'lng')
  const hasZoom = Object.prototype.hasOwnProperty.call(obj, 'zoom')
  if (hasLat !== hasLng || hasLat !== hasZoom) return false
  if (!hasLat) return true
  return typeof obj.lat === 'number' && Number.isFinite(obj.lat) &&
    typeof obj.lng === 'number' && Number.isFinite(obj.lng) &&
    typeof obj.zoom === 'number' && Number.isFinite(obj.zoom)
}

/**
 * Parse a single line of JSONL from the message stream into a typed StreamEvent.
 * Returns null if the line is not valid JSON or not a recognized event type.
 */
export function parseStreamEvent(line: string): StreamEvent | null {
  let parsed: unknown

  try {
    parsed = JSON.parse(line)
  } catch {
    return null
  }

  if (typeof parsed !== 'object' || parsed === null) return null

  const obj = parsed as Record<string, unknown>
  const { event, messageId, role, parts, oaiConversationId, rawContent } = obj

  if (event === STREAM_EVENT.ITINERARY_UPDATE_STARTED) {
    return { event: STREAM_EVENT.ITINERARY_UPDATE_STARTED }
  }

  if (event === STREAM_EVENT.ITINERARY_UPDATES) {
    const { updates } = obj
    if (Array.isArray(updates)) {
      return {
        event: STREAM_EVENT.ITINERARY_UPDATES,
        updates: updates as Array<ItineraryUpdate>,
      }
    }
  }

  if (event === STREAM_EVENT.CONTENT_COMPLETE) {
    return { event: STREAM_EVENT.CONTENT_COMPLETE }
  }

  // Handle enrichment_complete events (emitted as the entity itself, not wrapped)
  if (event === STREAM_EVENT.ENRICHMENT_COMPLETE) {
    if (isEnrichmentCompleteEvent(obj)) {
      return {
        event: STREAM_EVENT.ENRICHMENT_COMPLETE,
        data: obj,
      }
    }
  }

  if (event === STREAM_EVENT.CHAT_LOADING_STATE) {
    const data = obj.data
    if (isChatToolLoadingStateData(data)) {
      return {
        event: STREAM_EVENT.CHAT_LOADING_STATE,
        data,
      }
    }
  }

  // Handle hotel_search_results events
  if (event === STREAM_EVENT.HOTEL_SEARCH_RESULTS) {
    const data = obj.data
    if (isHotelSearchResultsData(data)) {
      return {
        event: STREAM_EVENT.HOTEL_SEARCH_RESULTS,
        data,
      }
    }
  }

  // Handle hotel_search_error events
  if (event === STREAM_EVENT.HOTEL_SEARCH_ERROR) {
    const data = obj.data
    if (isHotelSearchErrorData(data)) {
      return {
        event: STREAM_EVENT.HOTEL_SEARCH_ERROR,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.EXPERIENCE_SEARCH_ERROR) {
    const data = obj.data
    if (isExperienceSearchErrorData(data)) {
      return {
        event: STREAM_EVENT.EXPERIENCE_SEARCH_ERROR,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS) {
    const data = obj.data
    if (isExperienceSearchResultsData(data)) {
      return {
        event: STREAM_EVENT.EXPERIENCE_SEARCH_RESULTS,
        data,
      }
    }
  }

  if (event === STREAM_EVENT.CHAT_UI_ACTION) {
    const data = obj.data
    if (isChatUiActionData(data)) {
      return { event: STREAM_EVENT.CHAT_UI_ACTION, data }
    }
  }

  if (event === STREAM_EVENT.STATUS) {
    if (isStatusEventData(obj)) {
      if (obj.phase === 'reasoning') {
        const summaries = Array.isArray(obj.summaries)
          ? obj.summaries.filter((s): s is string => typeof s === 'string')
          : undefined
        return { event: STREAM_EVENT.STATUS, data: { phase: 'reasoning', ...(summaries && summaries.length > 0 && { summaries }) } }
      }
      const args = typeof obj.args === 'object' && obj.args !== null && !Array.isArray(obj.args)
        ? obj.args
        : {}
      return { event: STREAM_EVENT.STATUS, data: { phase: 'tool_calling', tool: obj.tool, args } }
    }
  }

  if (event === STREAM_EVENT.HEARTBEAT) {
    return { event: STREAM_EVENT.HEARTBEAT }
  }

  if (event === STREAM_EVENT.TOKEN_BATCH && typeof obj.text === 'string') {
    return { event: STREAM_EVENT.TOKEN_BATCH, text: obj.text }
  }

  if (typeof messageId !== 'string') return null

  if (event === STREAM_EVENT.MESSAGE_START) {
    return {
      event: STREAM_EVENT.MESSAGE_START,
      messageId,
      role: typeof role === 'string' ? role : 'ASSISTANT',
    }
  }

  if ((event === STREAM_EVENT.PARTS_UPDATE || event === STREAM_EVENT.MESSAGE_END) && Array.isArray(parts)) {
    const baseEvent = { messageId, parts: parts as Array<ContractMessagePart> }

    if (event === STREAM_EVENT.PARTS_UPDATE) {
      return { event: STREAM_EVENT.PARTS_UPDATE, ...baseEvent }
    }

    return {
      event: STREAM_EVENT.MESSAGE_END,
      ...baseEvent,
      ...(typeof oaiConversationId === 'string' ? { oaiConversationId } : {}),
      ...(typeof rawContent === 'string' ? { rawContent } : {}),
    }
  }

  return null
}
